import { useEffect, useState } from "react";
import type { EqmsController } from "../../../controllers/useEqms";
import {
  friendlyMessage,
  listVersions,
  planVersion,
  releaseVersion,
  startVersionDev,
  supersedeVersion,
} from "../../../controllers/eqmsApi";
import type { Version } from "../../../models/eqms";
import { VERSION_STATUS_LABEL } from "../../../models/eqms";

// 버전/릴리스: a project's release-version timeline, with a plan-version form and per-version
// lifecycle actions (start dev, release, supersede).
export function EqmsVersionsView({ eqms }: { eqms: EqmsController }) {
  const [versions, setVersions] = useState<Version[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const { getToken, projectId } = eqms;

  async function reload() {
    if (!projectId) return;
    setError(null);
    try {
      setVersions(await listVersions(getToken, projectId));
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    listVersions(getToken, projectId)
      .then((vs) => { if (!cancelled) setVersions(vs); })
      .catch((e) => { if (!cancelled) setError(friendlyMessage(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [getToken, projectId]);

  if (!projectId) return <div className="center">프로젝트를 선택하세요.</div>;

  async function run(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
      await reload();
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 19 }}>버전 / 릴리스</h2>
        <button className="btn primary" style={{ width: "auto" }} onClick={() => setCreating((c) => !c)}>
          {creating ? "취소" : "＋ 버전 계획"}
        </button>
      </div>

      {error && (
        <div className="note" style={{ background: "#fdeef0", marginBottom: 16 }}>
          <b style={{ color: "var(--amber)" }}>오류:</b> {error}
        </div>
      )}

      {creating && (
        <PlanVersionForm
          eqms={eqms}
          onDone={() => { setCreating(false); void reload(); }}
        />
      )}

      {loading ? (
        <div className="center">불러오는 중…</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {versions.map((v) => (
            <VersionRow key={v.id} version={v} onRun={run} getToken={getToken} />
          ))}
          {versions.length === 0 && <div className="sdui-list-empty">버전이 없습니다.</div>}
        </div>
      )}
    </div>
  );
}

function VersionRow({
  version,
  onRun,
  getToken,
}: {
  version: Version;
  onRun: (fn: () => Promise<unknown>) => void;
  getToken: EqmsController["getToken"];
}) {
  const [sprintId, setSprintId] = useState("");
  const [showDeploy, setShowDeploy] = useState(false);

  return (
    <div className="screen-row" style={{ flexDirection: "column", alignItems: "stretch", gap: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <b>{version.label}</b>{" "}
          <span className="sdui-badge">{VERSION_STATUS_LABEL[version.status]}</span>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {(version.status === "PLANNED" || version.status === "IN_DEV") && (
            <>
              {version.status === "PLANNED" && (
                <button className="btn ghost" style={{ width: "auto" }} onClick={() => onRun(() => startVersionDev(getToken, version.id))}>
                  개발시작
                </button>
              )}
              <button className="btn primary" style={{ width: "auto" }} onClick={() => setShowDeploy((v) => !v)}>
                배포(릴리스)
              </button>
            </>
          )}
          {version.status === "RELEASED" && (
            <button className="btn ghost" style={{ width: "auto" }} onClick={() => onRun(() => supersedeVersion(getToken, version.id))}>
              대체
            </button>
          )}
        </div>
      </div>
      <div style={{ fontSize: 13, color: "var(--ink3)", display: "flex", gap: 16 }}>
        <span>계획일: {version.plannedDate ?? "-"}</span>
        <span>릴리스일: {version.releasedAt ? new Date(version.releasedAt).toLocaleString() : "-"}</span>
      </div>
      {version.notes && <p style={{ fontSize: 13, margin: 0 }}>{version.notes}</p>}
      {showDeploy && (
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
          <label className="sdui-field" style={{ flex: 1 }}>
            <span className="sdui-label">배포 결정 스프린트 ID (선택)</span>
            <input className="sdui-input" value={sprintId} onChange={(e) => setSprintId(e.target.value)} />
          </label>
          <button
            className="btn primary"
            style={{ width: "auto" }}
            onClick={() => { onRun(() => releaseVersion(getToken, version.id, sprintId || null)); setShowDeploy(false); }}
          >
            확인
          </button>
          <button className="btn ghost" style={{ width: "auto" }} onClick={() => setShowDeploy(false)}>취소</button>
        </div>
      )}
    </div>
  );
}

function PlanVersionForm({ eqms, onDone }: { eqms: EqmsController; onDone: () => void }) {
  const [label, setLabel] = useState("");
  const [plannedDate, setPlannedDate] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!eqms.projectId || !label.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await planVersion(eqms.getToken, eqms.projectId, {
        label: label.trim(),
        plannedDate: plannedDate || null,
        notes: notes || null,
      });
      onDone();
    } catch (e) {
      setError(friendlyMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="info-card" style={{ marginBottom: 16 }}>
      <h4>버전 계획</h4>
      {error && (
        <div className="note" style={{ background: "#fdeef0", marginBottom: 12 }}>
          <b style={{ color: "var(--amber)" }}>오류:</b> {error}
        </div>
      )}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
        <label className="sdui-field" style={{ minWidth: 160 }}>
          <span className="sdui-label">라벨</span>
          <input className="sdui-input" value={label} onChange={(e) => setLabel(e.target.value)} />
        </label>
        <label className="sdui-field" style={{ minWidth: 160 }}>
          <span className="sdui-label">계획일 (선택)</span>
          <input className="sdui-input" type="date" value={plannedDate} onChange={(e) => setPlannedDate(e.target.value)} />
        </label>
        <label className="sdui-field" style={{ flex: 1, minWidth: 200 }}>
          <span className="sdui-label">메모 (선택)</span>
          <input className="sdui-input" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>
        <button className="btn primary" style={{ width: "auto" }} disabled={!label.trim() || busy} onClick={submit}>
          {busy ? "생성 중…" : "계획"}
        </button>
      </div>
    </div>
  );
}
